import { featureFlags } from '@seen/core/featureFlags'
import { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import { Tabs, TabsList, TabsTrigger, TypographyPageTitle } from '@seen/ui'
import { IconPrescription } from '@tabler/icons-react'
import { type JSX, useCallback, useMemo, useState } from 'react'
import { DropdownMenuItem } from '#src/components/core/index.ts'
import { Loader } from '#src/components/core/Loader.tsx'
import { MedicationDeliveryPreferenceField } from '#src/components/medications/MedicationDeliveryPreferenceField.tsx'
import { MedicationRequestGroupedList } from '#src/components/medications/MedicationRequestGroupedList.tsx'
import { MedicationsPrintFaxMenuItems } from '#src/components/medications/MedicationsPrintFaxMenuItems.tsx'
import { MedicationsPrintFaxModal } from '#src/components/medications/MedicationsPrintFaxModal.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { useCurrentMedications } from '#src/hooks/useCurrentMedications.ts'
import { useElationDraftPrescription } from '#src/hooks/useElationDraftPrescription.ts'
import { useElationPrescribe } from '#src/hooks/useElationPrescribe.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import {
  combineDraftIdsByThread,
  medicationForDraftOpen,
  useMedicationDraftsByThread,
} from '#src/hooks/useMedicationDraftsByThread.ts'
import { useMedicationsPrintFax } from '#src/hooks/useMedicationsPrintFax.ts'
import { useNavigateFromClickEvent } from '#src/hooks/useNavigateFromClickEvent.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

const EMPTY_MEDICATION_REQUESTS: ExtendedMedicationRequest[] = []

type MedicationFilter = 'all' | 'active' | 'inactive' | 'issues'

export function PatientMedicationsPage(): JSX.Element {
  const participant = useCurrentParticipant()
  const navigateFromClick = useNavigateFromClickEvent()
  const {
    openPrescribe,
    canPrescribe,
    isLoading: isPrescribeLoading,
  } = useElationPrescribe(participant.id)
  const {
    createDraftPrescription,
    openDraftPrescription,
    draftingMedicationIds,
    optimisticDraftIdsByThreadId,
    canDraft,
  } = useElationDraftPrescription(participant.id, {
    surface: 'medications_page',
  })
  const { draftByThreadId } = useMedicationDraftsByThread(participant.reference)
  const [selectedFilter, setSelectedFilter] = useState<MedicationFilter>('all')
  const printFaxEnabled = useFeatureFlag(featureFlags.medicationsCardPrintFax)
  const { activeRxMedications, activeOtcMedications } = useCurrentMedications(
    participant.reference
  )
  const printFax = useMedicationsPrintFax({
    participant,
    rxMedications: activeRxMedications,
    otcMedications: activeOtcMedications,
  })

  const {
    data: medicationRequests = EMPTY_MEDICATION_REQUESTS,
    isLoading: isLoadingMedications,
  } = useSearch(ExtendedMedicationRequest, {
    patient: participant.reference,
    _sort: '-_lastUpdated',
    _count: 1000,
    'status:not': ['entered-in-error', 'draft'],
  })

  const groupByElationThread = useFeatureFlag(
    featureFlags.medicationGroupByElationThread
  )

  const allGroups = useMemo(() => {
    const groups = ExtendedMedicationRequest.groupMedications(
      medicationRequests,
      { groupByElationThread }
    )
    return ExtendedMedicationRequest.sortGroupsByLatestChange(groups)
  }, [medicationRequests, groupByElationThread])

  const activeGroups = useMemo(
    () => allGroups.filter((g) => g.currentStatus === 'active'),
    [allGroups]
  )

  const inactiveGroups = useMemo(
    () => allGroups.filter((g) => g.currentStatus !== 'active'),
    [allGroups]
  )

  const issuesGroups = useMemo(
    () =>
      allGroups.filter(
        (g) => g.issues.length > 0 || g.hasUnresolvedPharmacyError
      ),
    [allGroups]
  )

  const medicationGroups = useMemo(() => {
    switch (selectedFilter) {
      case 'active':
        return activeGroups
      case 'inactive':
        return inactiveGroups
      case 'issues':
        return issuesGroups
      default:
        return allGroups
    }
  }, [selectedFilter, allGroups, activeGroups, inactiveGroups, issuesGroups])

  const openStampedDraft = useCallback(
    (draftId: number | string, medication?: ExtendedMedicationRequest) => {
      openDraftPrescription(
        draftId,
        medicationForDraftOpen({
          draftId,
          medication,
          draftsByThreadId: draftByThreadId,
        })
      )
    },
    [draftByThreadId, openDraftPrescription]
  )

  const draftIdsByThreadId = useMemo(
    () =>
      combineDraftIdsByThread({
        serverDraftsByThreadId: draftByThreadId,
        optimisticDraftIdsByThreadId,
      }),
    [draftByThreadId, optimisticDraftIdsByThreadId]
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <TypographyPageTitle>Medications</TypographyPageTitle>
        <div className="flex items-center gap-2">
          <MoreDropdownMenu
            open={printFaxEnabled ? printFax.menuOpen : undefined}
            onOpenChange={printFax.onMenuOpenChange}
            unmountOnClose={printFaxEnabled}
          >
            {canPrescribe && (
              <DropdownMenuItem
                onSelect={openPrescribe}
                loading={isPrescribeLoading}
              >
                <IconPrescription />
                Prescribe
              </DropdownMenuItem>
            )}
            {printFaxEnabled && (
              <MedicationsPrintFaxMenuItems
                onPrint={printFax.handlePrint}
                onFax={printFax.handleFax}
                onDismiss={printFax.dismissMenu}
                isPrinting={printFax.isPrinting}
              />
            )}
          </MoreDropdownMenu>
          <MedicationsPrintFaxModal faxModal={printFax.faxModal} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-4">
        <Tabs value={selectedFilter}>
          <TabsList>
            <TabsTrigger value="all" onClick={() => setSelectedFilter('all')}>
              All ({allGroups.length})
            </TabsTrigger>
            <TabsTrigger
              value="active"
              onClick={() => setSelectedFilter('active')}
            >
              Active ({activeGroups.length})
            </TabsTrigger>
            <TabsTrigger
              value="inactive"
              onClick={() => setSelectedFilter('inactive')}
            >
              Inactive ({inactiveGroups.length})
            </TabsTrigger>
            <TabsTrigger
              value="issues"
              onClick={() => setSelectedFilter('issues')}
            >
              Issues ({issuesGroups.length})
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <MedicationDeliveryPreferenceField participant={participant} />
      </div>

      {isLoadingMedications ? (
        <div className="flex items-center justify-center p-8">
          <Loader />
        </div>
      ) : (
        <MedicationRequestGroupedList
          groups={medicationGroups}
          onGroupClick={(group, requestId) => {
            const id = requestId ?? group.representativeRequest.id
            if (id) {
              navigateFromClick(
                `/Patient/${participant.id}/medications/${id}`,
                new MouseEvent('click')
              )
            }
          }}
          onDraftPrescription={canDraft ? createDraftPrescription : undefined}
          onOpenDraftPrescription={openStampedDraft}
          draftIdsByThreadId={draftIdsByThreadId}
          draftingMedicationIds={draftingMedicationIds}
        />
      )}
    </div>
  )
}
